import { useTranslation } from 'react-i18next';
import { RequestRefusalReason, RequestStatus } from '@magimp/shared';
import { Avatar, Icon } from '../../../ui/index.js';
import {
  formatDateTime,
  formatRelative,
  formatSLA,
  type SLAInfo,
  formatVolume,
  formatWeight,
} from '../format.js';
import { formatRequestDensity } from '../density.js';
import { formatRequestOrigin } from '../origin-display.js';
import type { TransitionAction } from '../role.js';
import { formatRequestDestination } from '../destination-display.js';
import type { RequestView } from '../types.js';
import { RequestStatusPill } from './RequestStatusPill.js';
import { RequestFunnelChips } from './RequestFunnelChips.js';
import {
  RequestDrawerHeaderActions,
  type RussiaRateButtonProps,
  type DuplicateButtonProps,
} from './RequestDrawerHeaderActions.js';

function slaLevelColor(level: SLAInfo['level']): string {
  switch (level) {
    case 'err':
      return 'var(--status-error-fg)';
    case 'warn':
      return 'var(--status-warn-fg)';
    case 'success':
      return 'var(--status-success-fg)';
    default:
      return 'var(--fg-2)';
  }
}

function refusalReasonLabelKey(reason: RequestRefusalReason) {
  switch (reason) {
    case RequestRefusalReason.QUOTE_RESPONSE_TOO_SLOW:
      return 'refuseModal.reason.quoteResponseTooSlow' as const;
    case RequestRefusalReason.PRICE_NOT_ACCEPTED:
      return 'refuseModal.reason.priceNotAccepted' as const;
    case RequestRefusalReason.CLIENT_UNRESPONSIVE:
      return 'refuseModal.reason.clientUnresponsive' as const;
    case RequestRefusalReason.OTHER:
      return 'refuseModal.reason.other' as const;
  }
}

export function RequestDrawerHeader(props: {
  r: RequestView;
  actions: TransitionAction[];
  sla: ReturnType<typeof formatSLA>;
  onAction: (a: TransitionAction) => void;
  onClose: () => void;
  narrow: boolean;
  russiaRate: RussiaRateButtonProps;
  duplicate: DuplicateButtonProps;
}) {
  const { t } = useTranslation(['requests', 'common']);
  const { r, actions, sla, onAction, onClose, narrow, russiaRate, duplicate } = props;
  const slaColor = slaLevelColor(sla.level);
  return (
    <div
      data-testid="request-drawer-header"
      style={{
        padding: '16px 24px 0',
        borderBottom: '1px solid var(--border-1)',
        flex: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        <div data-testid="request-drawer-header-text" style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 6,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-3)' }}>
              {r.id}
            </span>
            <RequestStatusPill status={r.status} />
            <RequestFunnelChips r={r} />
            {sla.level !== 'none' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 12,
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 500,
                  color: slaColor,
                }}
              >
                <Icon name={sla.level === 'err' ? 'timer-off' : 'timer'} size={12} /> SLA:{' '}
                {sla.text}
              </span>
            )}
            {r.status === RequestStatus.DRAFT && (
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--fg-3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Icon name="save" size={12} /> {t('requests:common.autoSave')}
              </span>
            )}
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 600,
              color: 'var(--fg-1)',
              letterSpacing: '-.005em',
              lineHeight: 1.25,
            }}
          >
            {r.name || t('requests:common.noName')}
          </h2>
          <div
            className="small"
            data-testid="request-route-row"
            style={{
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              ...(narrow ? { flexWrap: 'nowrap', overflow: 'hidden' } : { flexWrap: 'wrap' }),
            }}
          >
            {/* WHY (#1057 / #1496): CONTAINER rows surface the free-text
                delivery address. STANDARD/SIMPLE rows read canonical
                `destinations[]` via `formatRequestDestination` — NOT
                `route.to`, which is a denormalised search mirror that
                can drift from the canonical source. */}
            {(() => {
              const isContainer = r.kind === 'CONTAINER';
              const toText = formatRequestDestination(r, { short: narrow });
              const fromText = formatRequestOrigin(r, t('requests:draftDefaults.from'));
              return narrow ? (
                <>
                  <Icon name="map-pin" size={12} style={{ flex: 'none' }} />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fromText}
                  </span>
                  <span style={{ flex: 'none' }}> → </span>
                  <span
                    data-testid={isContainer ? 'request-delivery-address' : undefined}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {toText}
                  </span>
                  <span style={{ flex: 'none' }}>·</span>
                  <span style={{ flex: 'none', whiteSpace: 'nowrap' }}>
                    {t('requests:common.createdAt', { date: formatDateTime(r.createdAt) })}
                  </span>
                </>
              ) : (
                <>
                  <Icon name="map-pin" size={12} />
                  {fromText}
                  {' → '}
                  <span data-testid={isContainer ? 'request-delivery-address' : undefined}>
                    {toText}
                  </span>
                  <span>·</span>
                  <span>
                    {t('requests:common.createdAt', { date: formatDateTime(r.createdAt) })}
                  </span>
                </>
              );
            })()}
          </div>
        </div>
        <RequestDrawerHeaderActions
          r={r}
          actions={actions}
          narrow={narrow}
          onAction={onAction}
          onClose={onClose}
          russiaRate={russiaRate}
          duplicate={duplicate}
        />
      </div>

      <MetaRow r={r} />
      <RefusalDetails r={r} />
    </div>
  );
}

function MetaRow({ r }: { r: RequestView }) {
  const { t } = useTranslation('requests');
  return (
    <div style={{ display: 'flex', gap: 28, padding: '14px 0 12px', flexWrap: 'wrap' }}>
      <Cell
        label={t('common.manager')}
        value={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Avatar name={r.manager.name} size={18} />
            {r.manager.name}
          </span>
        }
      />
      <Cell
        label={t('common.logist_cn')}
        value={
          r.logist ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Avatar name={r.logist.name} size={18} />
              {r.logist.name}
            </span>
          ) : (
            <span style={{ color: 'var(--fg-4)' }}>{t('common.notAssigned')}</span>
          )
        }
      />
      <Cell
        label={t('common.items')}
        value={<span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.items.length || '—'}</span>}
      />
      <Cell label={t('common.weight')} value={formatWeight(r.weight)} mono />
      <Cell label={t('common.volume')} value={formatVolume(r.volume)} mono />
      <Cell
        label={t('common.density')}
        value={
          <span data-testid="request-density">{formatRequestDensity(r.weight, r.volume)}</span>
        }
        mono
      />
      <Cell label={t('common.updatedAt')} value={formatRelative(r.updatedAt)} />
    </div>
  );
}

function RefusalDetails({ r }: { r: RequestView }) {
  const { t } = useTranslation('requests');
  if (
    r.status !== RequestStatus.REFUSED ||
    (r.refusalReason === null && r.refusalComment === null)
  ) {
    return null;
  }

  return (
    <div
      data-testid="request-refusal-details"
      style={{
        display: 'grid',
        gap: 4,
        padding: '0 0 12px',
        color: 'var(--fg-1)',
      }}
    >
      <div className="eyebrow">{t('refusalDetails.title')}</div>
      <div data-testid="request-refusal-reason" className="body">
        {r.refusalReason === null
          ? t('refusalDetails.reasonMissing')
          : t(refusalReasonLabelKey(r.refusalReason))}
      </div>
      {r.refusalComment !== null ? (
        <div
          data-testid="request-refusal-comment"
          className="small"
          style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
        >
          {r.refusalComment}
        </div>
      ) : null}
    </div>
  );
}

function Cell({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'var(--fg-3)',
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--fg-1)',
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-ui)',
          fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
        }}
      >
        {value}
      </div>
    </div>
  );
}
